import React, { useState, useEffect } from 'react';
import { Alert, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useFetchDeviceCrmByEan from '../../hooks/useFetchDeviceCrmByEan';
import useUpdateDeviceCrmData from '../../hooks/useUpdateDeviceCrmData';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ContactAddModal from './ContactAddModal';
import useDeleteContactToCustomer from '../../hooks/useDeleteContactToCustomer';
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import "./DeviceScanControl.css"

interface DeviceDetailsCustomerProps {
  ean: string | undefined;
}

const DeviceDetailsCrm: React.FC<DeviceDetailsCustomerProps> = ({ ean }) => {
  const { t } = useTranslation();
  const [showAddContact, setShowAddContact] = useState(false);
  const { updateDeviceCrmData, error: updateError } = useUpdateDeviceCrmData();
  const { deleteContactToCustomer } = useDeleteContactToCustomer();
  const { crmResponse, error: fetchError, refetch } = useFetchDeviceCrmByEan({ ean: ean || '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(0);
  const [showPopupContactDelete, setShowPopupContactDelete] = useState(false);
  const [showPopupDataSave, setShowPopupDataSave] = useState(false);

  const deviceConfigSchema = yup.object().shape({
    name: yup.string().required(t("required")),
    siren: yup.string().required(t("required")).matches(/^\d{9}$/, t("invalidSiren")).max(9),
    adress: yup.string().required(t("required")),
    zipCode: yup.string().required(t("required")),
    city: yup.string().required(t("required")),
    country: yup.string().required(t("required")),
  });

  type MyFormData = yup.InferType<typeof deviceConfigSchema>;

  const { register, handleSubmit: hookFormSubmit, formState: { errors }, reset } = useForm<MyFormData, any, any>({
    mode: 'all',
    resolver: yupResolver(deviceConfigSchema),
  });

  useEffect(() => {
    if (crmResponse) {
      reset(crmResponse);
    }
  }, [crmResponse]);

  const handleSubmit = async (formData: MyFormData) => {
    const payloadToUpdate = {
      ...formData,
      id: 0,
      device: { id: crmResponse?.device?.id ? crmResponse.device.id : 0, ean: ean },
      ean: ean
    }
    const data = await updateDeviceCrmData(payloadToUpdate, ean);
    if (data) {
      setShowPopupDataSave(true);
      console.log("The data are updated");
      setTimeout(() => {
        setShowPopupDataSave(false);
      }, 3000);
      refetch();
    }
  };

  const handleModalClose = () => setShowAddContact(false);

  const handleDeleteContact = async () => {
    if (contactToDelete) {
      const success = await deleteContactToCustomer(contactToDelete);
      if (success) {
        setShowPopupContactDelete(true);
        setTimeout(() => {
          setShowPopupContactDelete(false);
        }, 3000);
        refetch();
      } else {
        console.error('Failed to delete contact');
        // Handle the error, e.g., show an error message
      }
      setShowDeleteModal(false);
      setContactToDelete(0);
    }
  };

  const openDeleteModal = (contactId: number) => {
    console.log(contactId);
    setContactToDelete(contactId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setContactToDelete(0);
  };

  return (
    <>
      <Row className="mt-3">
        <Col>
          <Card className="shadow">
            <Card.Header className="d-flex justify-content-between align-items-center pt-3 bg-primary border-bottom">
              <Card.Title className="position-relative top-0 start-50 translate-middle-x fs-4 text-white fw-semibold">{t("devices.crm.title")}</Card.Title>
              <Button type='submit' className="button-scan" form='form-crm'>{t("update")}</Button>
            </Card.Header>

            <Card.Body>
              <Form id='form-crm' onSubmit={hookFormSubmit(handleSubmit)}>
                {fetchError && <Alert variant="danger">{fetchError}</Alert>}
                {updateError && <Alert variant="danger">{updateError}</Alert>}
                {showPopupContactDelete && <Alert variant="success">{t("devices.crm.contact.messageDelete")}</Alert>}
                {showPopupDataSave && <Alert variant="success">{t("dataSaved")}</Alert>}
                <Row className="g-4">
                  <Col md={6}>
                    <Form.Group controlId="formSiren">
                      <Form.Label className="fs-5 fw-medium text-secondary">{t("devices.crm.siren")}</Form.Label>
                      <Form.Control
                        type="text"
                        {...register("siren")}
                        isInvalid={!!errors.siren}
                        className='text-secondary bg-light fw-medium py-2'
                        placeholder={t("devices.crm.enterSiren")}
                        maxLength={9}
                      />
                      <Form.Control.Feedback type='invalid'>{errors.siren?.message}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="formName">
                      <Form.Label className="fs-5 fw-medium text-secondary">{t("name")}</Form.Label>
                      <Form.Control
                        type="text"
                        {...register("name")}
                        isInvalid={!!errors.name}
                        className='text-secondary bg-light fw-medium'
                        placeholder={t("tools.enterName")}
                      />
                      <Form.Control.Feedback type='invalid'>{errors.name?.message}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group controlId="formAdress">
                      <Form.Label className="fs-5 fw-medium text-secondary">{t("devices.customer.label.address")}</Form.Label>
                      <Form.Control
                        type="text"
                        {...register("adress")}
                        isInvalid={!!errors.adress}
                        className='text-secondary bg-light fw-medium'
                        placeholder={t("devices.crm.enterAddress")}
                      />
                      <Form.Control.Feedback type='invalid'>{errors.adress?.message}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="formZipcode">
                      <Form.Label className="fs-5 fw-medium text-secondary">{t("devices.crm.zipCode")}</Form.Label>
                      <Form.Control
                        type="text"
                        {...register("zipCode")}
                        isInvalid={!!errors.zipCode}
                        className='text-secondary bg-light fw-medium'
                        placeholder={t("devices.crm.enterZipCode")}
                      />
                      <Form.Control.Feedback type='invalid'>{errors.zipCode?.message}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="formCity">
                      <Form.Label className="fs-5 fw-medium text-secondary">{t("devices.crm.city")}</Form.Label>
                      <Form.Control
                        type="text"
                        {...register("city")}
                        isInvalid={!!errors.city}
                        className='text-secondary bg-light fw-medium'
                        placeholder={t("devices.crm.enterCity")}
                      />
                      <Form.Control.Feedback type='invalid'>{errors.city?.message}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="formCountry">
                      <Form.Label className="fs-5 fw-medium text-secondary">{t("devices.crm.country")}</Form.Label>
                      <Form.Control
                        type="text"
                        {...register("country")}
                        isInvalid={!!errors.country}
                        className='text-secondary bg-light fw-medium'
                        placeholder={t("devices.crm.enterCountry")}
                      />
                      <Form.Control.Feedback type='invalid'>{errors.country?.message}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Card.Body>

            <Card.Header className='d-flex justify-content-between align-items-end bg-white'>
              <Card.Title className="fs-4 fw-bold mb-1 text-secondary">Contact</Card.Title>
            </Card.Header>
            <Table responsive hover striped className="text-center position-static top-50 start-50 mx-4 mt-3 align-middle" style={{ width: "95%" }}>
              <thead className='table-light'>
                <tr className='fs-5 text-nowrap'>
                  <th className="p-2 bg-primary text-white fw-semibold rounded-3 rounded-bottom-0 rounded-end-0 px-5">{t("name")}</th>
                  <th className="p-2 bg-primary text-white fw-semibold px-5">{t("devices.customer.label.address")}</th>
                  <th className="p-2 bg-primary text-white fw-semibold px-5">{t("email")}</th>
                  <th className="p-2 bg-primary text-white fw-semibold px-5">{t("phone")}</th>
                  <th className="p-2 bg-primary text-white fw-semibold rounded-3 rounded-bottom-0 rounded-start-0 px-5">Actions</th>
                </tr>
              </thead>

              <tbody>
                {crmResponse?.contacts?.map(contact => (
                  <tr key={contact.id}>
                    <td className='text-light-emphasis fw-medium'>{contact.lastName} {contact.firstName}</td>
                    <td className='text-light-emphasis fw-medium'>{contact.adress} {contact.zipCode} {contact.city} {contact.country}</td>
                    <td className='text-light-emphasis fw-medium'>{contact.eMail}</td>
                    <td className='text-light-emphasis fw-medium'>{contact.phone}</td>
                    <td>
                      <Button type='button' variant='secondary' className='mx-3' onClick={() => openDeleteModal(contact.id ?? 0)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
                {(!crmResponse?.contacts || crmResponse?.contacts.length === 0) && (
                  <tr>
                    <td colSpan={5}>{t("devices.crm.contact.noContact")}</td>
                  </tr>
                )}
              </tbody>
            </Table>
            <Card.Body className='d-flex justify-content-end'>
              <Button className="px-3 border-0" variant='secondary' onClick={() => setShowAddContact(!showAddContact)}>
                <FontAwesomeIcon icon={faPlus} /> {t("add")}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showAddContact && (
        <ContactAddModal ean={ean} show={true} customer={crmResponse} handleClose={handleModalClose} onSuccess={refetch} />
      )}
      {showDeleteModal && (
        <Modal show={showDeleteModal} onHide={closeDeleteModal}>
          <Modal.Header className='d-flex justify-content-center'>
            <Modal.Title className='fs-4'>{t("devices.crm.contact.confirmDeletionContact")}</Modal.Title>
          </Modal.Header>
          <Modal.Body className='text-center'>{t("devices.crm.contact.deleteContact")}</Modal.Body>
          <Col className='d-flex justify-content-end p-2'>
            <Button variant="secondary" className='px-2 fw-medium mx-2' onClick={closeDeleteModal}>{t("cancel")}</Button>
            <Button variant="danger" className='px-2 fw-medium' onClick={handleDeleteContact} >{t("delete")}</Button>
          </Col>
        </Modal>
      )}
    </>
  );
};

export default DeviceDetailsCrm;
